import { beginNavigation, endNavigation, redirectTo } from '../../shared/navigation';
import { useEffect, useState } from 'react';
import { resolveProductUrl } from '../../core/productUrls';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageMeta } from '../../core/usePageMeta';
import { ROUTES } from '../../app/routes';
import { whenSessionBooted } from '../../app/sessionSlice';
import { isNotFound, publicApi } from './publicApi';
import { EmptyState, ErrorState, PublicLayout } from './components';

type State =
  | { kind: 'loading' }
  | { kind: 'redirect'; url: string }
  | { kind: 'no-website' }
  | { kind: 'missing' }
  | { kind: 'error' };

/** Only absolute http(s) targets are followed; the API validated them, this is defence in depth. */
function isNavigable(url: string | null): url is string {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

/** Overridable in tests; jsdom cannot navigate. */
export const companyRedirect = { assign: (url: string) => redirectTo(url) };

/**
 * `/o/:slug`: a company card's target. PlatformFE no longer renders a business page here: each
 * product owns it (Restaurant-Client, Cosmetic-Client… at `<product host>/o/:slug`, or `/b/:slug`
 * while unpublished). This route only resolves the destination and leaves in the current tab, so
 * cards, search suggestions and older shared links keep working.
 */
export function CompanyPage() {
  const { t } = useTranslation();
  const { slug = '' } = useParams();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);
  usePageMeta(t('hub.company.opening', 'Opening business'), { noindex: true });

  useEffect(() => {
    let active = true;
    beginNavigation();
    setState({ kind: 'loading' });
    (async () => {
      const destination = await publicApi.destination(slug);
      // Published Cosmetics websites also use the product host, but the API labels them custom.
      const usesProductHost = destination.kind === 'product'
        || (destination.kind === 'custom' && destination.productCode === 'COSMETICS');
      if (usesProductHost && destination.url && destination.productCode) {
        destination.url = resolveProductUrl(destination.productCode, destination.url);
      }
      if (!active) return;
      if (destination.kind !== 'platform' && isNavigable(destination.url)) {
        setState({ kind: 'redirect', url: destination.url });
        // Finish the session check before leaving for the business host.
        await whenSessionBooted();
        if (!active) return;
        companyRedirect.assign(destination.url);
        return;
      }
      endNavigation();
      setState({ kind: 'no-website' });
    })().catch((error: unknown) => {
      if (!active) return;
      endNavigation();
      setState(isNotFound(error) ? { kind: 'missing' } : { kind: 'error' });
    });
    return () => { active = false; };
  }, [slug, attempt]);

  return <PublicLayout>
    <div className="hub-wrap hub-section">
      {state.kind === 'loading' && <div className="hub-skeleton" role="status" aria-label={t('hub.loading', 'Loading')} style={{ minHeight: 160 }} />}
      {state.kind === 'redirect' && <EmptyState>{t('hub.company.redirecting', 'Opening the business website…')} <a href={state.url}>{t('hub.company.continue', 'Continue')}</a></EmptyState>}
      {state.kind === 'no-website' && <EmptyState>
        {t('hub.company.noWebsite', 'This business has no public page yet.')} <Link to={ROUTES.landing}>{t('hub.company.browse', 'Browse the directory')}</Link>
      </EmptyState>}
      {state.kind === 'missing' && <EmptyState>{t('hub.company.notFound', 'This business is not listed.')} <Link to={ROUTES.landing}>{t('hub.company.browse', 'Browse the directory')}</Link></EmptyState>}
      {state.kind === 'error' && <ErrorState message={t('hub.company.loadError', 'The business page could not be loaded.')} onRetry={() => setAttempt((value) => value + 1)} />}
    </div>
  </PublicLayout>;
}
